/**
 * Accounting News Service
 * 
 * Fetches and caches accounting/financial news from industry sources
 * Supports RSS feeds from IFAC, IASB, FASB, and major accounting publications
 */

import { supabase } from './supabaseClient';

export interface AccountingNewsArticle {
  id: string;
  title: string;
  description: string;
  link: string;
  pubDate: string;
  source: string;
  category: string;
  author?: string;
  imageUrl?: string;
  tags?: string[];
  content?: string;
}

export interface NewsSource {
  id: string;
  name: string;
  url: string;
  category: string;
  description?: string;
  isActive: boolean;
}

// ============================================
// RSS FEED PARSER
// ============================================
export const parseRSSFeed = async (xmlString: string): Promise<AccountingNewsArticle[]> => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
  
  const parseError = xmlDoc.querySelector('parsererror');
  if (parseError) {
    return [];
  }

  let items = xmlDoc.querySelectorAll('item');
  if (items.length === 0) {
    items = xmlDoc.querySelectorAll('entry');
  }
  if (items.length === 0) {
    return [];
  }

  const articles: AccountingNewsArticle[] = [];

  items.forEach((item) => {
    try {
      const title = item.querySelector('title')?.textContent?.trim();
      const link = item.querySelector('link')?.textContent?.trim() || 
                   item.querySelector('link')?.getAttribute('href')?.trim();
      const description = item.querySelector('description')?.textContent?.trim() ||
                        item.querySelector('summary')?.textContent?.trim();
      const pubDate = item.querySelector('pubDate')?.textContent?.trim() ||
                    item.querySelector('published')?.textContent?.trim() ||
                    item.querySelector('updated')?.textContent?.trim();
      const author = item.querySelector('author')?.textContent?.trim() ||
                   item.querySelector('name')?.textContent?.trim();
      const guid = item.querySelector('guid')?.textContent?.trim() ||
                 item.querySelector('id')?.textContent?.trim() ||
                 link;

      if (title && link) {
        let imageUrl: string | undefined;
        if (description) {
          const imgPatterns = [
            /<img[^>]+src="([^">]+)"/i,
            /<img[^>]+src='([^'>]+)'/i,
            /<enclosure[^>]+url="([^">]+)"/i,
          ];
          
          for (const pattern of imgPatterns) {
            const match = description.match(pattern);
            if (match) {
              imageUrl = match[1];
              break;
            }
          }
        }

        let cleanDescription = description;
        if (description) {
          cleanDescription = description
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .trim();
        }

        const tags = extractAccountingTags(title + ' ' + (cleanDescription || ''));

        articles.push({
          id: guid || link,
          title,
          description: cleanDescription || '',
          link,
          pubDate: pubDate ? new Date(pubDate).toISOString() : new Date().toISOString(),
          author,
          imageUrl,
          tags,
          source: '',
          category: '',
          content: cleanDescription
        });
      }
    } catch (error) {
      // Skip invalid items silently
    }
  });

  return articles;
};

// ============================================
// ACCOUNTING TAGS EXTRACTOR
// ============================================
export const extractAccountingTags = (text: string): string[] => {
  const accountingKeywords = [
    'IFRS', 'GAAP', 'IFRS 17', 'IFRS 9', 'IFRS 16', 'IFRS 15', 'SAS', 'SOX', 'Sarbanes-Oxley',
    'IASB', 'FASB', 'IFAC', 'PCAOB', 'SEC', 'ESMA', 'FRC', 'ASBJ', 'MCA',
    'Financial Reporting', 'Audit', 'Assurance', 'Tax', 'Compliance', 'Risk Management',
    'Internal Audit', 'External Audit', 'Forensic Accounting', 'Management Accounting',
    'Cost Accounting', 'Government Accounting', 'Nonprofit Accounting',
    'Revenue Recognition', 'Lease Accounting', 'Financial Instruments',
    'Consolidation', 'M&A', 'Due Diligence', 'Internal Controls', 'COSO',
    'XBRL', 'Digital Reporting', 'ESG', 'Sustainability Reporting',
    'Dodd-Frank', 'MiFID II', 'GDPR', 'Anti-Money Laundering', 'AML', 'KYC',
    'Basel III', 'Solvency', 'Regulatory Reporting',
    'CPA', 'CA', 'ACCA', 'CIMA', 'CMA', 'CIA', 'CFE', 'CISA',
    'Banking', 'Insurance', 'Investment Management', 'Real Estate',
    'Healthcare', 'Technology', 'Manufacturing', 'Energy', 'Mining'
  ];

  const tags: string[] = [];
  const upperText = text.toUpperCase();

  accountingKeywords.forEach(keyword => {
    if (upperText.includes(keyword.toUpperCase())) {
      tags.push(keyword);
    }
  });

  return [...new Set(tags)];
};

// ============================================
// NEWS FETCHER
// ============================================
export const fetchAccountingNewsFromSource = async (source: NewsSource): Promise<AccountingNewsArticle[]> => {
  try {
    let response: Response | null = null;
    let directFetchFailed = false;
    
    try {
      response = await fetch(source.url, {
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36',
          'Accept': 'application/rss+xml, application/xml, text/xml',
        },
      });
      
      if (!response.ok) {
        directFetchFailed = true;
      }
    } catch (directError) {
      directFetchFailed = true;
    }
    
    if (directFetchFailed || !response) {
      const proxyUrls = [
        `https://api.allorigins.win/raw?url=${encodeURIComponent(source.url)}`,
        `https://corsproxy.io/?${encodeURIComponent(source.url)}`,
        `https://thingproxy.freeboard.io/fetch/${encodeURIComponent(source.url)}`
      ];
      
      for (const proxyUrl of proxyUrls) {
        try {
          response = await fetch(proxyUrl, {
            headers: {
              'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
              'Accept': 'application/rss+xml, application/xml, text/xml, */*',
            },
          });
          
          if (response.ok) {
            break;
          }
        } catch (proxyError) {
          continue;
        }
      }
    }
    
    if (!response || !response.ok) {
      return [];
    }

    const xmlText = await response.text();

    if (!xmlText || xmlText.trim().length === 0) {
      return [];
    }

    if (xmlText.toLowerCase().includes('<html') || xmlText.toLowerCase().includes('<!doctype html')) {
      return [];
    }

    const articles = await parseRSSFeed(xmlText);

    return articles.map(article => ({
      ...article,
      source: source.name,
      category: source.category
    }));
  } catch (error) {
    return [];
  }
};

// ============================================
// BATCH FETCHER (All Sources)
// ============================================
export const fetchAllAccountingNews = async (): Promise<{
  success: boolean;
  articlesAdded: number;
  errors: string[];
  sourcesProcessed: number;
}> => {
  try {
    const { data: sources, error: sourcesError } = await supabase
      .from('accounting_news_sources')
      .select('*')
      .eq('is_active', true);

    if (sourcesError) {
      throw sourcesError;
    }

    let totalArticles = 0;
    const errors: string[] = [];
    let sourcesProcessed = 0;

    for (const source of sources || []) {
      try {
        const articles = await fetchAccountingNewsFromSource(source);

        for (const article of articles) {
          const { error } = await supabase
            .from('accounting_news_articles')
            .upsert({
              title: article.title,
              link: article.link,
              description: article.description,
              content: article.content,
              pub_date: article.pubDate,
              source: article.source,
              category: article.category,
              author: article.author,
              image_url: article.imageUrl,
              tags: article.tags,
              guid: article.id,
              language: 'en'
            }, {
              onConflict: 'guid',
              ignoreDuplicates: true,
            });

          if (!error) {
            totalArticles++;
          }
        }

        await supabase
          .from('accounting_news_sources')
          .update({ last_fetched_at: new Date().toISOString() })
          .eq('id', source.id);

        sourcesProcessed++;
      } catch (error) {
        errors.push(`${source.name}: ${error}`);
      }
    }

    await supabase.rpc('clean_old_accounting_news');
    
    return {
      success: true,
      articlesAdded: totalArticles,
      errors,
      sourcesProcessed
    };
  } catch (error) {
    return {
      success: false,
      articlesAdded: 0,
      errors: [String(error)],
      sourcesProcessed: 0
    };
  }
};

// ============================================
// GETTERS FOR DISPLAY
// ============================================
export const getAccountingNewsByCategory = async (
  category?: string,
  limit: number = 20,
  offset: number = 0
): Promise<AccountingNewsArticle[]> => {
  try {
    let query = supabase
      .from('accounting_news_articles')
      .select('*')
      .order('pub_date', { ascending: false });

    if (category && category !== '') {
      query = query.eq('category', category);
    }

    const { data, error } = await query
      .range(offset, offset + limit - 1)
      .limit(limit);

    if (error) {
      throw error;
    }

    return (data || []).map(article => ({
      id: article.id,
      title: article.title,
      description: article.description,
      link: article.link,
      pubDate: article.pub_date,
      source: article.source,
      category: article.category,
      author: article.author,
      imageUrl: article.image_url,
      tags: article.tags || []
    }));
  } catch (error) {
    return [];
  }
};

export const getFeaturedAccountingNews = async (limit: number = 5): Promise<AccountingNewsArticle[]> => {
  try {
    const { data, error } = await supabase
      .from('accounting_news_articles')
      .select('*')
      .in('category', ['ifac', 'iasb', 'fasb', 'regulatory'])
      .gte('pub_date', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
      .order('pub_date', { ascending: false })
      .limit(limit);

    if (error) {
      throw error;
    }

    return (data || []).map(article => ({
      id: article.id,
      title: article.title,
      description: article.description,
      link: article.link,
      pubDate: article.pub_date,
      source: article.source,
      category: article.category,
      author: article.author,
      imageUrl: article.image_url,
      tags: article.tags || []
    }));
  } catch (error) {
    return [];
  }
};

export const getAccountingNewsSources = async (): Promise<NewsSource[]> => {
  try {
    const { data, error } = await supabase
      .from('accounting_news_sources')
      .select('id, name, url, category, description, is_active')
      .eq('is_active', true)
      .order('name');

    if (error) throw error;

    return (data || []).map(source => ({
      id: source.id,
      name: source.name,
      url: source.url,
      category: source.category,
      description: source.description || '',
      isActive: source.is_active
    }));
  } catch (error) {
    return [];
  }
};

// ============================================
// SEARCH FUNCTIONALITY
// ============================================
export const searchAccountingNews = async (
  query: string,
  limit: number = 20
): Promise<AccountingNewsArticle[]> => {
  try {
    const { data, error } = await supabase
      .from('accounting_news_articles')
      .select('*')
      .or(`title.ilike.%${query}%,description.ilike.%${query}%,tags.cs.{${query}}`)
      .order('pub_date', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return (data || []).map(article => ({
      id: article.id,
      title: article.title,
      description: article.description,
      link: article.link,
      pubDate: article.pub_date,
      source: article.source,
      category: article.category,
      author: article.author,
      imageUrl: article.image_url,
      tags: article.tags
    }));
  } catch (error) {
    return [];
  }
};

// ============================================
// CATEGORIES
// ============================================
export const ACCOUNTING_NEWS_CATEGORIES = [
  { value: 'ifac', label: 'IFAC', description: 'International Federation of Accountants' },
  { value: 'iasb', label: 'IASB/IFRS', description: 'International Accounting Standards Board' },
  { value: 'fasb', label: 'FASB/GAAP', description: 'Financial Accounting Standards Board' },
  { value: 'accounting', label: 'Accounting', description: 'General accounting news' },
  { value: 'audit', label: 'Audit', description: 'Internal and external audit' },
  { value: 'tax', label: 'Tax', description: 'Taxation and tax policy' },
  { value: 'regulatory', label: 'Regulatory', description: 'SEC, PCAOB, and regulatory bodies' },
  { value: 'compliance', label: 'Compliance', description: 'Compliance and risk management' },
  { value: 'business', label: 'Business', description: 'General business and finance' }
];
